import { useTranslation } from "react-i18next";
import ActionButton from "./ActionButton";
import AttendanceMatrix from "./AttendanceMatrix";
import Field from "./Field";
import RsvpRecap from "./RsvpRecap";

// Small local helper — just the "+ Add another guest" link that used
// to live inside MemberList. The full MemberList UI is no longer
// rendered in the coded attendance step; names are edited inline in
// the matrix instead.
const AddGuestLink = ({ onClick, disabled }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="body-16"
      style={{
        background: "none",
        border: "none",
        color: "var(--black)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 0.65,
        padding: 0,
        fontSize: 13,
        letterSpacing: 0.3,
        textTransform: "uppercase",
      }}
    >
      {t("rsvp.addGuest")}
    </button>
  );
};

// ─── Sub-renderers ─────────────────────────────────────────────

const Loading = () => {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: "center", padding: "60px 0" }}>
      <p
        id="rsvp-modal-title"
        className="heading-60"
        style={{ marginBottom: 20 }}
      >
        {t("rsvp.coded.loading")}
      </p>
      <p className="body-16" style={{ opacity: 0.8 }}>
        {t("rsvp.coded.loadingSubtext")}
      </p>
    </div>
  );
};

// Shown when the coded fetch failed or the user rejected the loaded
// invitation. Intentionally warm and non-alarming — this screen renders
// for normal "That's not me" moments as well as true load failures, so
// the tone needs to work in both cases.
const ErrorScreen = ({ headline, detail, onTryAnotherCode, onClose }) => {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: "center", padding: "40px 0" }}>
      <p
        id="rsvp-modal-title"
        className="heading-60"
        style={{ marginBottom: 16 }}
      >
        {headline}
      </p>
      {detail && (
        <p
          className="body-16"
          style={{ opacity: 0.8, marginBottom: 30, maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}
        >
          {detail}
        </p>
      )}
      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <ActionButton variant="primary" onClick={onTryAnotherCode}>
          {t("rsvp.buttons.tryAnotherCode")}
        </ActionButton>
        <ActionButton variant="ghost" onClick={onClose}>
          {t("rsvp.buttons.close")}
        </ActionButton>
      </div>
    </div>
  );
};

const AlreadySubmitted = ({ rsvpData }) => {
  const sections = rsvpData?.sections || [];
  const members = rsvpData?.party?.members || [];
  const attendance = rsvpData?.attendance || [];
  return (
    <RsvpRecap
      sections={sections}
      members={members}
      responses={attendance}
    />
  );
};

const IdentityStep = ({ rsvpData, onContinue, onNotMe }) => {
  const { t } = useTranslation();
  const firstName = rsvpData.guest.firstName;
  const members = rsvpData.party.members;
  const hasParty = members.length > 1;

  return (
    <div style={{ textAlign: "center", padding: "16px 0 8px" }}>
      <h2
        id="rsvp-modal-title"
        className="heading-60"
        style={{ margin: 0, marginBottom: 10 }}
      >
        {t("rsvp.coded.welcome", { firstName })}
      </h2>
      <p
        className="body-16"
        style={{
          opacity: 0.65,
          margin: 0,
          marginBottom: 28,
          maxWidth: 420,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {hasParty
          ? t("rsvp.coded.withGuests")
          : t("rsvp.coded.singleGuest")}
      </p>

      <div
        style={{
          maxWidth: 320,
          margin: "0 auto 32px",
          padding: "18px 20px",
          borderTop: "1px solid rgba(0,0,0,0.12)",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
        }}
      >
        {hasParty && (
          <p
            className="body-16"
            style={{
              opacity: 0.5,
              fontSize: 11,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              margin: 0,
              marginBottom: 10,
            }}
          >
            {t("rsvp.coded.yourParty")}
          </p>
        )}
        {members.map((member) => (
          <p
            key={member._id}
            className="body-16"
            style={{ margin: 0, marginBottom: 4, lineHeight: 1.6 }}
          >
            {member.name}
          </p>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <ActionButton variant="primary" onClick={onContinue}>
          {t("rsvp.buttons.continue")}
        </ActionButton>
      </div>

      <button
        type="button"
        onClick={onNotMe}
        className="body-16"
        style={{
          background: "none",
          border: "none",
          color: "var(--black)",
          opacity: 0.55,
          cursor: "pointer",
          padding: 0,
          fontSize: 13,
          textDecoration: "underline",
          textUnderlineOffset: 3,
        }}
      >
        {t("rsvp.coded.notMe", { firstName })}
      </button>
    </div>
  );
};

const AttendanceStep = ({
  sections,
  members,
  responses,
  flow,
  error,
}) => {
  const { t } = useTranslation();
  const { setStatus, addNewMember, updateNewMember, removeNewMember, setStep } =
    flow.actions;
  const { isCodedAttendanceComplete, hasCodedAttendanceStarted } = flow;
  const showIncompleteHelper =
    !isCodedAttendanceComplete && hasCodedAttendanceStarted;
  return (
    <div>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <h2
          id="rsvp-modal-title"
          className="heading-60"
          style={{
            margin: 0,
            fontSize: 30,
            lineHeight: 1.2,
            marginBottom: 6,
          }}
        >
          {t("rsvp.attendance.title")}
        </h2>
        <p className="body-16" style={{ opacity: 0.6, margin: 0 }}>
          {t("rsvp.attendance.subtitle")}
        </p>
      </div>
      <AttendanceMatrix
        sections={sections}
        members={members}
        responses={responses}
        onChange={setStatus}
        disabled={flow.submitting}
        onMemberNameChange={(memberId, nextName) => {
          // memberId is "new-<index>" for added guests; extract the index.
          const idx = Number(memberId.replace("new-", ""));
          if (Number.isFinite(idx)) updateNewMember(idx, "name", nextName);
        }}
        onMemberRemove={(memberId) => {
          const idx = Number(memberId.replace("new-", ""));
          if (Number.isFinite(idx)) removeNewMember(idx);
        }}
        footer={
          <AddGuestLink
            onClick={addNewMember}
            disabled={flow.submitting}
          />
        }
      />
      {error && (
        <p
          className="body-16"
          style={{
            color: "#a23b31",
            textAlign: "center",
            marginBottom: 12,
          }}
        >
          {error}
        </p>
      )}
      {showIncompleteHelper && !error && (
        <p
          className="body-16"
          style={{
            opacity: 0.6,
            textAlign: "center",
            marginTop: 14,
            marginBottom: 0,
            fontSize: 13,
          }}
        >
          {t("rsvp.attendance.validation")}
        </p>
      )}
      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: showIncompleteHelper ? 12 : 8,
        }}
      >
        <ActionButton
          variant="ghost"
          onClick={() => setStep(0)}
          disabled={flow.submitting}
        >
          {t("rsvp.buttons.back")}
        </ActionButton>
        <ActionButton
          variant="primary"
          onClick={() => setStep(2)}
          disabled={flow.submitting || !isCodedAttendanceComplete}
        >
          {t("rsvp.buttons.continue")}
        </ActionButton>
      </div>
    </div>
  );
};

const MessageStep = ({ flow, error }) => {
  const { t } = useTranslation();
  const { codedMessage, submitting, actions } = flow;
  return (
    <div>
      <p
        id="rsvp-modal-title"
        className="heading-60"
        style={{ marginBottom: 20, textAlign: "center" }}
      >
        {t("rsvp.message.title")}
      </p>
      <p
        className="body-16"
        style={{ opacity: 0.8, marginBottom: 30, textAlign: "center" }}
      >
        {t("rsvp.message.subtitle")}
      </p>

      <div style={{ maxWidth: 520, margin: "0 auto 30px" }}>
        <Field
          as="textarea"
          placeholder={t("rsvp.message.placeholder")}
          value={codedMessage}
          onChange={(e) => actions.setCodedMessage(e.target.value)}
          rows={4}
          maxLength={2000}
          disabled={submitting}
          style={{
            width: "100%",
            resize: "vertical",
            minHeight: 100,
            fontFamily: "inherit",
          }}
        />
      </div>

      {error && (
        <p
          className="body-16"
          style={{
            color: "#a23b31",
            textAlign: "center",
            marginBottom: 20,
          }}
        >
          {error}
        </p>
      )}

      <div
        style={{
          display: "flex",
          gap: 16,
          justifyContent: "center",
          flexWrap: "wrap",
          marginTop: 20,
        }}
      >
        <ActionButton
          variant="ghost"
          onClick={() => actions.setStep(1)}
          disabled={submitting}
        >
          {t("rsvp.buttons.back")}
        </ActionButton>
        <ActionButton
          variant="primary"
          onClick={actions.submitCoded}
          disabled={submitting}
        >
          {submitting ? t("rsvp.buttons.submitting") : t("rsvp.buttons.submit")}
        </ActionButton>
      </div>
    </div>
  );
};

const Confirmation = ({ sections, members, summary }) => (
  <RsvpRecap
    sections={sections}
    members={members}
    responses={summary || []}
  />
);

// ─── Main component ────────────────────────────────────────────

/**
 * CodedFlow
 *
 * Orchestrates the coded-RSVP experience: loading, error recovery,
 * already-submitted short-circuit, identity (step 0), attendance
 * matrix (step 1), optional message (step 2), and submit
 * confirmation (step 3).
 *
 * Props:
 *   flow    — the useRsvpFlow return value
 *   onClose — called when the user wants to dismiss the modal entirely
 */
const CodedFlow = ({ flow, onClose }) => {
  const { t } = useTranslation();
  const {
    loading,
    error,
    rsvpData,
    alreadySubmitted,
    responses,
    submissionSummary,
    step,
    allMembers,
    actions,
  } = flow;

  if (loading) return <Loading />;
  // Only surface the error screen when the fetch actually failed — not
  // during normal transitions where rsvpData is briefly null.
  if (error && !rsvpData) {
    return (
      <ErrorScreen
        headline={t("rsvp.coded.errorTitle")}
        detail={error}
        onTryAnotherCode={actions.leaveCodedFlow}
        onClose={onClose}
      />
    );
  }
  if (alreadySubmitted) return <AlreadySubmitted rsvpData={rsvpData} />;
  // If rsvpData is null but there's no error and no loading, we're in a
  // transitional state (e.g. just after leaveCodedFlow before the mode
  // switch renders). Render nothing — the parent will route us elsewhere
  // on the next render.
  if (!rsvpData) return null;

  const sections = rsvpData.sections || [];

  switch (step) {
    case 0:
      return (
        <IdentityStep
          rsvpData={rsvpData}
          onContinue={() => actions.setStep(1)}
          onNotMe={actions.leaveCodedFlow}
        />
      );
    case 1:
      return (
        <AttendanceStep
          sections={sections}
          members={allMembers}
          responses={responses}
          flow={flow}
          error={error}
        />
      );
    case 2:
      return <MessageStep flow={flow} error={error} />;
    case 3:
      return (
        <Confirmation
          sections={sections}
          members={allMembers}
          summary={submissionSummary}
        />
      );
    default:
      return null;
  }
};

export default CodedFlow;
